// PRD 14 §2.2 #4 / §4.1 — version history table (6 columns).
// Row click opens the detail view. No pagination — full history per type.

import { useTranslation } from 'react-i18next';

import type { TermVersionSummary } from '../types/terms.types';
import { AuthorCell } from './AuthorCell';
import { TermStatusBadge } from './TermStatusBadge';

interface TermsVersionListProps {
  versions: TermVersionSummary[];
  onRowSelect: (versionId: number) => void;
}

// PRD §4.3 — YYYY-MM-DD on the wire → YYYY.MM.DD on screen.
function formatEffectiveDate(value: string): string {
  // Defensive: if the wire string is unexpected, fall back to it verbatim.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return value.replace(/-/g, '.');
}

// PRD §4.3 — ISO datetime → YYYY.MM.DD HH:MM (24h). Renders in the user's
// local timezone (Date constructor parses the ISO offset).
function formatRegisteredAt(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${mo}.${da} ${hh}:${mm}`;
}

export function TermsVersionList({ versions, onRowSelect }: TermsVersionListProps) {
  const { t } = useTranslation('terms');

  return (
    <table className="table">
      <thead>
        <tr>
          <th style={{ width: 90 }}>{t('list.columns.version')}</th>
          <th>{t('list.columns.revisionNote')}</th>
          <th style={{ width: 280 }}>{t('list.columns.author')}</th>
          <th style={{ width: 120 }}>{t('list.columns.effectiveDate')}</th>
          <th style={{ width: 170 }}>{t('list.columns.registeredAt')}</th>
          <th style={{ width: 100 }}>{t('list.columns.status')}</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((v) => (
          <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => onRowSelect(v.id)}>
            <td>
              <span style={{ fontWeight: 600, color: 'var(--info)' }}>{v.version}</span>
            </td>
            <td>
              {v.revisionNote?.trim() ? (
                <span>{v.revisionNote}</span>
              ) : (
                <span style={{ color: 'var(--muted-foreground)' }}>{t('list.noRevisionNote')}</span>
              )}
            </td>
            <td>
              <AuthorCell author={v.author} />
            </td>
            <td>{formatEffectiveDate(v.effectiveDate)}</td>
            <td>{formatRegisteredAt(v.registeredAt)}</td>
            <td>
              <TermStatusBadge status={v.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
